import { reactive, ref, Ref } from '@vue/composition-api';
import dayjs from 'dayjs';
import i18n, { i18n_t } from '@/locales/i18n';
import smh, { SMH } from '@/renderer/scripts/smh';
import tips from '@/renderer/scripts/utils/tips';
import { formatSize, formatSize2Unit } from '@/renderer/scripts/utils/tools/index';
import { ECOption } from '@/renderer/components/charts/type';
interface TrafficInfo {
  totalSize: string;
  totalUnit: string;
  usedSize: string;
  usedUnit: string;
  restSize: string;
  restUnit: string;
  percentage: number;
}
type Handler = {
  purchaseTrafficInfo: TrafficInfo;
  freeTrafficInfo: TrafficInfo;
  timing: Ref<string>;
  disabledDate: string;
  startTime: Ref<string>;
  endTime: Ref<string>;
  timeRange: Ref<string[]>;
  chartLoading: Ref<boolean>;
  chartOptions: ECOption;
  methods: {
    trafficUsageOverview: () => Promise<void>;
    trafficUsageTiming: () => Promise<void>;
    getTimeRange: (type: string) => void;
  };
};
export default function spaceHandler(): Handler {
  const purchaseTrafficInfo = reactive({
    totalSize: '0',
    totalUnit: 'MB',
    usedSize: '0',
    usedUnit: 'MB',
    restSize: '0',
    restUnit: 'MB',
    percentage: 0,
  });
  const freeTrafficInfo = reactive({
    totalSize: '0',
    totalUnit: 'MB',
    usedSize: '0',
    usedUnit: 'MB',
    restSize: '0',
    restUnit: 'MB',
    percentage: 0,
  });

  // 折线图相关
  const todayStart = dayjs().format('YYYY-MM-DD 00:00:00');
  const todayEnd = dayjs().format('YYYY-MM-DD 23:59:59');
  const timing = ref('today');
  // 今天以后的日期不可选
  const disabledDate = dayjs(todayStart).format('YYYY-MM-DD');
  const startTime = ref(todayStart);
  const endTime = ref(todayEnd);
  const timeRange = ref([todayStart, todayEnd]);

  // 折线图配置
  const chartLoading = ref(false);
  const chartOptions: ECOption = reactive({
    grid: {
      containLabel: true,
      top: 10,
      right: 0,
      bottom: 40,
      left: 0,
    },
    legend: {
      bottom: 0,
      icon: 'rect',
      itemWidth: 12,
      itemHeight: 6,
      itemGap: 40,
      data: [
        {
          name: i18n_t('外网总流量'),
        },
        {
          name: i18n_t('企业空间外网流量'),
        },
        {
          name: i18n_t('个人空间外网流量'),
        },
      ],
    },
    color: ['#014fd1', '#ED7B2F', '#7742B3'],
    xAxis: {
      type: 'time',
      boundaryGap: true,
      data: [],
      axisTick: {
        show: true,
        alignWithLabel: true,
      },
      splitLine: {
        show: false,
      },
    },
    yAxis: {
      type: 'value',
      splitNumber: 4,
      axisLabel: {
        inside: false,
      },
      axisLine: {
        show: false,
      },
      // 轴
      splitLine: {
        lineStyle: {
          color: '#E7E7E7',
        },
      },
    },
    tooltip: {
      trigger: 'axis',
    },
    series: [],
  });
  const methods = {
    // 流量使用概览
    async trafficUsageOverview() {
      try {
        const res = await smh.userAPI.trafficUsageOverview();
        const paidTotal = res.data.paidTotal || '0';
        const paidRemain = res.data.paidRemain || '0';
        const freeTotal = res.data.freeTotal || '0';
        const freeRemain = res.data.freeRemain || '0';
        const paidUsage = +paidTotal - +paidRemain;
        const freeUsage = +freeTotal - +freeRemain;
        const paidTotalFormat = formatSize(paidTotal);
        const paidUsageFormat = formatSize(paidUsage);
        const paidRemainFormat = formatSize(paidRemain);
        const freeTotalFormat = formatSize(freeTotal);
        const freeUsageFormat = formatSize(freeUsage);
        const freeRemainFormat = formatSize(freeRemain);
        Object.assign(purchaseTrafficInfo, {
          totalSize: paidTotalFormat.size,
          totalUnit: paidTotalFormat.unit,
          usedSize: paidUsageFormat.size,
          usedUnit: paidUsageFormat.unit,
          restSize: paidRemainFormat.size,
          restUnit: paidRemainFormat.unit,
          percentage: paidTotal === '0' ? 0 : +((+paidUsage / +paidTotal) * 100).toFixed(2),
        });
        Object.assign(freeTrafficInfo, {
          totalSize: freeTotalFormat.size,
          totalUnit: freeTotalFormat.unit,
          usedSize: freeUsageFormat.size,
          usedUnit: freeUsageFormat.unit,
          restSize: freeRemainFormat.size,
          restUnit: freeRemainFormat.unit,
          percentage: freeTotal === '0' ? 0 : +((+freeUsage / +freeTotal) * 100).toFixed(2),
        });
      } catch (e) {
        tips.error(e as MsgError);
      }
    },
    // 时序流量统计
    async trafficUsageTiming() {
      chartLoading.value = true;
      try {
        const res = await smh.userAPI.trafficUsageTiming({
          startTime: `${dayjs(startTime.value).format('YYYY-MM-DD HH:mm:ss')}`,
          endTime: `${dayjs(endTime.value).format('YYYY-MM-DD HH:mm:ss')}`,
        });
        const { datapoints, timestamps } = res.data;
        const xData = timestamps.map((item: number) => dayjs(item).format('YYYY-MM-DD HH:mm:ss'));
        const totalTrafficList = datapoints[0].values.map((item: number) => formatSize2Unit(item, 'GB'));
        const teamTrafficList = datapoints[1].values.map((item: number) => formatSize2Unit(item, 'GB'));
        const personalTrafficList = datapoints[2].values.map((item: number) => formatSize2Unit(item, 'GB'));
        chartLoading.value = false;
        methods.setChartOptions(xData, totalTrafficList, teamTrafficList, personalTrafficList);
      } catch (e) {
        chartLoading.value = false;
        tips.error(e as MsgError);
      }
    },
    setChartOptions(
      xData: string[],
      totalTrafficList: number[],
      teamTrafficList: number[],
      personalTrafficList: number[],
    ) {
      Object.assign(chartOptions, {
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: xData,
          axisTick: {
            show: true,
            alignWithLabel: true,
          },
          splitLine: {
            show: false,
          },
        },
        series: [
          {
            data: totalTrafficList,
            type: 'line',
            smooth: false,
            symbol: 'none',
            name: i18n_t('外网总流量'),
            sampling: true,
            lineStyle: {
              normal: {
                width: 1,
              },
            },
          },
          {
            data: teamTrafficList,
            type: 'line',
            smooth: false,
            symbol: 'none',
            name: i18n_t('企业空间外网流量'),
            sampling: true,
            lineStyle: {
              normal: {
                width: 1,
              },
            },
          },
          {
            data: personalTrafficList,
            type: 'line',
            smooth: false,
            symbol: 'none',
            name: i18n_t('个人空间外网流量'),
            sampling: true,
            lineStyle: {
              normal: {
                width: 1,
              },
            },
          },
        ],
      });
    },
    getTimeRange(type: string) {
      if (type === 'today') {
        startTime.value = dayjs().format('YYYY-MM-DD 00:00:00');
        endTime.value = dayjs().format('YYYY-MM-DD 23:59:59');
      } else if (type === 'week') {
        endTime.value = dayjs().format('YYYY-MM-DD 23:59:59');
        startTime.value = dayjs(endTime.value).subtract(7, 'days').format('YYYY-MM-DD 00:00:00');
      } else if (type === 'month') {
        endTime.value = dayjs().format('YYYY-MM-DD 23:59:59');
        startTime.value = dayjs(endTime.value).subtract(30, 'days').format('YYYY-MM-DD 00:00:00');
      } else if (type === 'year') {
        endTime.value = dayjs().format('YYYY-MM-DD 23:59:59');
        startTime.value = dayjs(endTime.value).subtract(365, 'days').format('YYYY-MM-DD 00:00:00');
      }
    },
  };
  methods.getTimeRange(timing.value);
  return {
    purchaseTrafficInfo,
    freeTrafficInfo,
    timing,
    disabledDate,
    timeRange,
    startTime,
    endTime,
    chartLoading,
    chartOptions,
    methods,
  };
}
